import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Upload, X, ArrowLeft } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { api, imageUrl } from "@/lib/api";
import { CATEGORIES, UNITS, DISTRICTS, parseNum } from "@/lib/constants";
import { toast } from "sonner";

const empty = { title: "", category: "", description: "", quantity: "", unit: "", price: "", district: "", images: [] };

const ListingForm = () => {
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = !!id;
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (isEdit) {
      api.get(`/products/${id}`).then((res) => {
        const d = res.data;
        setForm({ title: d.title, category: d.category, description: d.description, quantity: d.quantity, unit: d.unit, price: d.price, district: d.district, images: d.images || [] });
      });
    }
  }, [id, isEdit]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/upload", fd);
        setForm((f) => ({ ...f, images: [...f.images, data.path] }));
      }
    } catch (err) {
      toast.error(t("empty.error"));
    } finally {
      setUploading(false);
    }
  };

  const removeImage = (idx) => setForm((f) => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSaving(true);
    try {
      const payload = { ...form, quantity: parseNum(form.quantity), price: parseNum(form.price) };
      if (isEdit) {
        await api.put(`/products/${id}`, payload);
        toast.success(t("toast.productUpdated"));
      } else {
        await api.post("/products", payload);
        toast.success(t("toast.productCreated"));
      }
      navigate("/dashboard");
    } catch (err) {
      setError(t("empty.error"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-xl mx-auto px-4 py-6">
      <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1 text-sm text-[#6B7280] mb-4 hover:text-[#123A2C]">
        <ArrowLeft className="w-4 h-4" /> {t("nav.dashboard")}
      </button>
      <h1 className="text-xl sm:text-2xl font-bold text-[#14251F] mb-5">{isEdit ? t("form.editTitle") : t("form.addTitle")}</h1>

      <form onSubmit={submit} className="bg-white rounded-2xl border border-[#E5E7EB] p-5 space-y-4">
        {error && <div className="text-sm text-[#DC2626] bg-red-50 rounded-lg p-2.5">{error}</div>}

        {/* Photos */}
        <div>
          <label className="text-sm text-[#6B7280] mb-2 block">{t("form.photos")}</label>
          <div className="flex flex-wrap gap-3">
            {form.images.map((img, i) => (
              <div key={i} className="relative w-20 h-20 rounded-lg overflow-hidden border border-[#E5E7EB]">
                <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" data-testid={`remove-image-${i}`} onClick={() => removeImage(i)} className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/60 text-white flex items-center justify-center">
                  <X className="w-3 h-3" />
                </button>
              </div>
            ))}
            <label data-testid="upload-photo" className="w-20 h-20 rounded-lg border-2 border-dashed border-[#E5E7EB] flex flex-col items-center justify-center cursor-pointer text-[#9A9E97] hover:border-[#123A2C]">
              <Upload className="w-5 h-5" />
              <span className="text-[10px] mt-1">{uploading ? t("form.uploading") : t("form.uploadPhoto")}</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} />
            </label>
          </div>
        </div>

        <div>
          <label className="text-sm text-[#6B7280] mb-1 block">{t("form.productTitle")}</label>
          <input data-testid="form-title" value={form.title} onChange={set("title")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
        </div>

        <div>
          <label className="text-sm text-[#6B7280] mb-1 block">{t("form.category")}</label>
          <select data-testid="form-category" value={form.category} onChange={set("category")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] bg-white">
            <option value="">{t("form.selectCategory")}</option>
            {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c[lang]}</option>)}
          </select>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("form.quantity")}</label>
            <input data-testid="form-quantity" type="text" inputMode="decimal" value={form.quantity} onChange={set("quantity")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("form.unit")}</label>
            <select data-testid="form-unit" value={form.unit} onChange={set("unit")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] bg-white">
              <option value="">{t("form.selectUnit")}</option>
              {UNITS.map((u) => <option key={u.key} value={u.key}>{u[lang]}</option>)}
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("form.price")}</label>
            <input data-testid="form-price" type="text" inputMode="decimal" value={form.price} onChange={set("price")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C]" />
          </div>
          <div>
            <label className="text-sm text-[#6B7280] mb-1 block">{t("form.district")}</label>
            <select data-testid="form-district" value={form.district} onChange={set("district")} required className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] bg-white">
              <option value="">{t("form.selectDistrict")}</option>
              {DISTRICTS.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
        </div>

        <div>
          <label className="text-sm text-[#6B7280] mb-1 block">{t("form.description")}</label>
          <textarea data-testid="form-description" value={form.description} onChange={set("description")} rows={4} className="w-full rounded-lg border border-[#E5E7EB] px-3 py-2.5 outline-none focus:border-[#123A2C] resize-none" />
        </div>

        <div className="flex gap-3 pt-2">
          <button type="button" onClick={() => navigate("/dashboard")} className="flex-1 py-3 rounded-xl bg-white border border-[#E5E7EB] text-[#6B7280] font-medium">
            {t("form.cancel")}
          </button>
          <button data-testid="form-submit" type="submit" disabled={saving || uploading} className="flex-1 py-3 rounded-xl bg-[#123A2C] text-white font-semibold hover:bg-[#0D2C21] transition-colors disabled:opacity-60">
            {isEdit ? t("form.update") : t("form.submit")}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ListingForm;
